import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Fuel, Settings2, Search, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FleetPage() {
  const { t } = useLanguage();
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: t('all') },
    { id: 'economy', label: t('economy') },
    { id: 'comfort', label: t('comfort') },
    { id: 'luxury', label: t('luxury') },
    { id: 'suv', label: t('suv') },
    { id: 'motorcycle', label: t('motorcycle') },
  ];

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await axios.get(`${API}/cars`);
        setCars(response.data);
        setFilteredCars(response.data);
      } catch (error) {
        console.error('Error fetching cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
    let result = cars;
    
    if (selectedCategory !== 'all') {
      result = result.filter(car => car.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(car => 
        car.name.toLowerCase().includes(query) ||
        car.brand.toLowerCase().includes(query)
      );
    }
    
    setFilteredCars(result);
  }, [selectedCategory, searchQuery, cars]);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="fleet-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-serif text-4xl md:text-5xl font-bold mb-4">
            {t('ourFleet')}
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl">
            {t('fleetDescription')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'bg-accent hover:bg-accent/90' : ''}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-sm ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search cars..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Cars Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
                <div className="h-56 bg-muted"></div>
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-10 bg-muted rounded mt-4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">No cars found matching your criteria</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCars.map((car, index) => (
              <div
                key={car.id}
                className="group bg-card border border-border/50 rounded-xl overflow-hidden card-hover animate-slide-up opacity-0"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`fleet-car-${car.id}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={car.image_url}
                    alt={car.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 bg-white/90 text-sm font-medium rounded-full capitalize">
                      {car.category}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <div className="flex items-center gap-3 text-white text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {car.seats}
                      </span>
                      <span className="flex items-center gap-1">
                        <Settings2 className="w-4 h-4" />
                        {car.transmission === 'automatic' ? t('automatic') : t('manual')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Fuel className="w-4 h-4" />
                        {car.fuel_type}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-serif text-xl font-semibold">{car.name}</h3>
                      <p className="text-sm text-muted-foreground">{car.brand} • {car.year || 2024}</p>
                    </div>
                    <div className="text-right">
                      {car.discount_percent > 0 ? (
                        <>
                          <span className="text-lg text-muted-foreground line-through">€{car.price_per_day}</span>
                          <span className="text-3xl font-bold text-red-500 ml-1">€{(car.price_per_day * (1 - car.discount_percent / 100)).toFixed(0)}</span>
                          <span className="block text-sm text-muted-foreground">{t('perDay')}</span>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">-{car.discount_percent}%</span>
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-gold">€{car.price_per_day}</span>
                          <span className="block text-sm text-muted-foreground">{t('perDay')}</span>
                        </>
                      )}
                      {car.season_price_per_day && car.season_price_per_day !== car.price_per_day && (
                        <span className="block text-sm font-medium text-orange-500 mt-1">
                          {car.discount_percent > 0 ? (
                            <><span className="line-through">€{car.season_price_per_day}</span> €{(car.season_price_per_day * (1 - car.discount_percent / 100)).toFixed(0)}</>
                          ) : (
                            <>€{car.season_price_per_day}</>
                          )} {t('seasonPrice')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {car.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="px-2 py-1 bg-secondary text-xs rounded">
                        {feature}
                      </span>
                    ))}
                    {car.features.length > 3 && (
                      <span className="px-2 py-1 bg-secondary text-xs rounded">
                        +{car.features.length - 3}
                      </span>
                    )}
                  </div>

                  <Link to={`/car/${car.id}`}>
                    <Button className="w-full bg-primary hover:bg-primary/90" data-testid={`book-car-${car.id}`}>
                      {t('bookThisCar')}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
